import { siteConfig } from './../../common/common';
import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';

import swiperStyles from '../../../node_modules/swiper/swiper-bundle.min.css?inline';
import Swiper from 'swiper';
import { Pagination, Navigation } from 'swiper/modules'; // Import Pagination module

import importedMarkupUI from './UI.html?raw';
import { rosAnimation, STAnimationType } from '../../utils/ScrollTriggerHelper';

const DEBUG_VERBOSE = false;
export const HHDSImageCarouselTagName: string = 'hhds-image-carousel';
const TAG_NAME: string = HHDSImageCarouselTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSImageCarouselAttrNames = {
	eyebrow: 'eyebrow',
	uniqueCount: 'unique-count',
};

const Attrs = HHDSImageCarouselAttrNames;

export class HHDSImageCarousel extends Component {
	private swiperContainer: HTMLElement | null = null;
	private swiperInstance: Swiper | null = null;
	private rosTweens: gsap.core.Tween[] = [];

	constructor() {
		super();
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const html: string = /* html */ `
			<div class="wrapper ${TAG_NAME}">
				${this.markupEyebrow}
        <slot class="slides"></slot>
				<div class="swiper">
					<div class="swiper-wrapper"></div>
				</div>
				${importedMarkupUI}
			</div>
    `;

		this.setInnerHtml('', '', html, [swiperStyles]);

		this.swiperContainer = this.root?.querySelector('.swiper') as HTMLElement;

		this.observeSlotChanges(true);
	}

	get markupEyebrow() {
		const eyebrow = this.vars.get<string>(Attrs.eyebrow);

		if (eyebrow) {
			return /* html */ `<div class="${TAG_NAME}-centred"><h2 class="${TAG_NAME}__heading">${eyebrow}</h2></div>`;
		} else {
			return '';
		}
	}

	swiperInit() {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.swiperInit.name);

		if (!this.swiperContainer) throw new Error('No swiperContainer');

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		// Ensure we clean up Swiper if it already exists as can cause issues
		if (this.swiperInstance) this.swiperDestroy();

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		/**
		 * Define out pagination and navigation elements
		 *
		 * We need to specifically query this as just using selectors can cause
		 * issues when multiple carousels are on the same page
		 */

		const btnPreviousEl = this.root?.querySelector('hhds-button-pagination[direction="previous"]') as HTMLElement;
		const btnNextEl = this.root?.querySelector('hhds-button-pagination[direction="next"]') as HTMLElement;
		const paginationEl = this.root?.querySelector('.hhds-image-carousel__ui-pagination') as HTMLElement;

		if (!btnPreviousEl || !btnNextEl || !paginationEl) throw new Error('Unable to derive pagination elements!');

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		this.swiperInstance = new Swiper(this.swiperContainer, {
			modules: [Pagination, Navigation],
			loop: true,
			slideFullyVisibleClass: 'swiper-slide-visible',
			slidesPerView: 'auto',
			spaceBetween: 8,
			lazyPreloadPrevNext: 1,
			centeredSlides: true,
			speed: 1150,
			loopPreventsSliding: true,
			breakpoints: {
				// when window width is >=
				834: {
					spaceBetween: 20,
				},
				// when window width is >=
				1500: {
					spaceBetween: 24,
				},
			},

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

			pagination: {
				el: paginationEl,
				type: 'custom',
				renderCustom: (swiper, _current, _total) => {
					const formatNumber = (num: number) => num.toString().padStart(2, '0');
					// We manually add duplicates at slot-insertion (i.e. HTML declaration) stage,
					// and mark how many of these are uniques. We use this to show pagination in terms
					// of those uniques. If 2 CMS images are supplied, we may want to create 6 slides
					// from this to make looping work well, but we don't want to display 5/6 etc. when there
					// are only really two images.
					const uniqueCount = this.vars.get<number>(Attrs.uniqueCount);
					const index = (swiper.realIndex % uniqueCount) + 1;
					return `${formatNumber(index)} / ${formatNumber(uniqueCount)}`;
				},
			},

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

			navigation: {
				nextEl: btnNextEl,
				prevEl: btnPreviousEl,
			},

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

			on: {
				init: (swiper: Swiper) => {
					if (siteConfig.enableRevealOnScroll && siteConfig.autoStartCarousel) {
						swiper.slides.forEach((slide, index) => {
							// Find the slide marked as the final-slide and move to it.
							// In loop mode, slides are repeated so we can't just count
							// the initial elements added.
							if (slide.classList.contains('final-slide')) {
								swiper.slideTo(index, 0, false);
							}
						});
					}
				},
				slideChange: (_swiper: Swiper) => {
					/*this.setButtonDisabledState();
          this.handleVisibleSlides();
          DEBUG_VERBOSE && logger.log("slideChange", swiper);*/
				},
				resize: () => {
					//this.handleVisibleSlides();
				},
			},
		});

		this.swiperInstance.init();

		// If ROS animation is enabled, start on the final slide,
		// and slide to the first when reaching the appropriate reveal
		// y-position on the page.
		if (siteConfig.enableRevealOnScroll && siteConfig.autoStartCarousel) {
			rosAnimation(this, STAnimationType.triggered, {
				start: '65%',
				end: '100%',
				onStart: () => {
					this.swiperInstance?.slideNext();
				},
			});
		}
	}

	swiperDestroy() {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.swiperDestroy.name);
		this.swiperInstance?.destroy(true, true);
		this.swiperInstance = null;
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this.rosTweens.forEach((tween) => tween.kill());
		this.rosTweens = [];
		this.swiperDestroy();
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);

		if (elements.length == 0) return;

		const swiperWrapperSelector = '.swiper-wrapper';
		const swiperWrapper = this.root?.querySelector(swiperWrapperSelector);
		if (!swiperWrapper) throw new Error(`Unable to derive selector: ${swiperWrapperSelector}`);

		elements.forEach((element, index) => {
			element.classList.add('swiper-slide');
			if (index == elements.length - 1) element.classList.add('final-slide');

			//Animation
			//element.classList.add('animate');
			//let delay = index === elements.length - 1 ? 0 : (_index + 1) * 0.1;
			//element.setAttribute('ros-delay', delay.toFixed(1));

			swiperWrapper.appendChild(element);
		});

		this.swiperInit();
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.eyebrow]: {
		description: 'Text above carousel',
		defaultValue: '',
		type: String,
	},
	[Attrs.uniqueCount]: {
		description: 'Number of images before repeats, used for counter',
		defaultValue: 0,
		type: Number,
	},
};
